import { useSyncExternalStore } from "react";

export type ThemePref = "dark" | "light" | "system";
const KEY = "omf.theme";
const listeners = new Set<() => void>();

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // default below
  }
  return "dark";
}

function resolve(pref: ThemePref): "dark" | "light" {
  if (pref !== "system") return pref;
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function applyTheme(pref: ThemePref = getThemePref()): void {
  document.documentElement.dataset.theme = resolve(pref);
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    // applies for this tab only
  }
  applyTheme(pref);
  listeners.forEach((l) => l());
}

export function initTheme(): void {
  applyTheme();
  if (typeof matchMedia === "function") {
    matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
      if (getThemePref() === "system") {
        applyTheme("system");
        listeners.forEach((l) => l());
      }
    });
  }
}

/** The theme actually rendered ("dark" | "light"). */
export function useResolvedTheme(): "dark" | "light" {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => (document.documentElement.dataset.theme === "light" ? "light" : "dark"),
  );
}

export function useThemePref(): ThemePref {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getThemePref,
  );
}
